import { useState } from "react";
import { FieldReference } from "@/components/organisms/Field/FieldReferences";
import { Badge } from "@/components/ui/badge";
import {
  inferTypename,
  getTitleValue,
  getObjectDef,
} from "./ToolCallCard.tools";
import { ToolCallCardNodeLayout } from "./ToolCallCardNodeLayout";
import { ToolCallCardTable } from "./ToolCallCardTable";
import type { NodeItem } from "./ToolCallCard.types";

/**
 * One presented record: the title bar always, its fields when opened — the
 * same row the pending changes tray lists its records with.
 */
export function PresentedRow({
  item,
  toolName,
  loadOnOpen = false,
  titleEndDecorator,
}: {
  item: NodeItem;
  toolName?: string | null;
  /** The item names the record; read the rest only when the row is opened. */
  loadOnOpen?: boolean;
  /** Where the record stands, shown at the end of its title bar. */
  titleEndDecorator?: React.ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      data-node-identifier={item.identifier}
      // ``clip`` keeps the row trimmed without becoming a scroll container,
      // which would trap the card's sticky title inside this row.
      className="overflow-clip"
    >
      <ToolCallCardNodeLayout
        item={item}
        toolName={toolName}
        deltaMode={false}
        size="sm"
        hideBody={!expanded}
        hideEmptyFields
        loadOnOpen={loadOnOpen}
        titleEndDecorator={titleEndDecorator}
        onTitleClick={() => setExpanded((v) => !v)}
      />
    </div>
  );
}

/**
 * Extract and render node references from tool output.
 * Shows a table for multiple items, chips for single/few items.
 *
 * Note: ``toolOutput.related_items`` is intentionally NOT rendered.
 * The backend uses ``ServiceResult.related_instances`` (which becomes
 * ``related_items`` in the tool output) as the unified "also push these
 * to the frontend" channel — for both genuine side effects (an
 * auto-wired ``WorkflowEdge``) and cache-hydration siblings (the
 * parent ``Workflow`` whose ``nodes`` list embeds the changed node).
 * Surfacing them here would clutter the AI tool card with secondary
 * objects the user didn't directly mutate. The AI itself still sees
 * them in ``tool_output`` for next-turn context.
 */
export function ToolCallCardNodeReferences({
  toolOutput,
  toolName,
  showTable = true,
}: {
  toolOutput: Record<string, unknown>;
  toolName?: string | null;
  showTable?: boolean;
}) {
  let mainItems: NodeItem[] = [];

  if (toolOutput.item) {
    mainItems = Array.isArray(toolOutput.item)
      ? toolOutput.item
      : [toolOutput.item as NodeItem];
  } else if (toolOutput.items && Array.isArray(toolOutput.items)) {
    mainItems = toolOutput.items as NodeItem[];
  }

  if (mainItems.length === 0) return null;

  // Presenting is the deliberate step that puts records in front of the user,
  // so they get records: openable rows carrying the same title bar and fields
  // as a created or edited one, not a chip or a two-column table of names.
  // A bulk write's rows are the records it wrote, as presented rows are:
  // named from what the call returned, each read when it is opened.
  const isBulkWrite = /^bulk_(create|update)_/.test(toolName ?? "");
  if (toolOutput.present_to_user === true || isBulkWrite) {
    // Not a scroll box: each row sticks its title under the card's header as
    // the thread scrolls, and a scroll container of its own would trap that.
    //
    // Each row draws a line under itself to part it from the next; under the
    // last there is no next, and that line landed on the card's own border and
    // read as twice as thick. Pulled up by its width, the two are one line.
    return (
      <div className="-mb-px">
        {mainItems
          .filter((item) => item.identifier)
          .map((item) => (
            <PresentedRow
              key={item.identifier}
              item={item}
              toolName={toolName}
              loadOnOpen={isBulkWrite}
            />
          ))}
      </div>
    );
  }

  if (showTable && mainItems.length >= 3) {
    return (
      <div className="flex flex-col">
        <ToolCallCardTable items={mainItems} toolName={toolName} />
      </div>
    );
  }

  const renderItem = (item: NodeItem, idx: number) => {
    if (!item.identifier) return null;

    const typename = inferTypename(item, toolName);
    const objectDef = getObjectDef(typename);
    const displayName = getTitleValue(item, objectDef);
    const base64Id = btoa(`${typename}:${item.identifier}`);

    return (
      <FieldReference
        key={item.identifier || idx}
        value={{
          __typename: typename,
          name: displayName,
          id: base64Id,
          identifier: item.identifier,
        }}
        valueOptions={{
          style: "related",
          isNullable: false,
        }}
        isMultiline={false}
        size="sm"
      />
    );
  };

  return (
    <div className="flex flex-wrap gap-1 pl-2 pr-2 pt-1.5 pb-1.5">
      {mainItems.slice(0, 10).map(renderItem)}
      {mainItems.length > 10 && (
        <Badge variant="secondary">+{mainItems.length - 10} more</Badge>
      )}
    </div>
  );
}
